<template lang="pug">
#inspection-reservation-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設見学の代理登録

    .box-body.register-body
      //- applicationDatetime
      FormDatetimePicker(v-model="applicationDatetime" :date.sync="applicationDatetimeDate" :time.sync="applicationDatetimeTime"
        name="application-datetime" key-name="申込み日時" placeholder="申込み日時" :validation-rules="{ required: true }" required)
      //- approved
      .d-flex.flex-column.mb-3(style="margin-left:20px;")
        span.approved-name 受付状態
        ApprovedSwitch(v-model="approved")
      //- datetime
      FormDatetimePicker(v-model="datetime" :date.sync="datetimeDate" :time.sync="datetimeTime" name="datetime" key-name="見学日時" placeholder="見学日時"
        :required="_requiredDatetime")
      //- name
      FormInput(v-model="name" key-name="氏名" name="name" type="text" placeholder="氏名" :maxlength="32"
        :validation-rules="{ required: true, max: 32 }" :styles="{ width: '340px' }" required)
      //- tel
      FormInput(v-model="tel" key-name="電話番号" name="tel" type="text" placeholder="電話番号" :maxlength="13"
        :validation-rules="{ required: true, tel: true, max: 14 }" :styles="{ width: '340px' }" required)
      //- applicantMessage
      FormTextarea(v-model="applicantMessage" key-name="申込者からの連絡事項" name="applicant-message" placeholder="申込者からの連絡事項" :maxlength="1000" :rows="7"
        :validation-rules="{ max: 1000 }" :styles="{ maxWidth: '640px', width: '640px' }")
      //- customerName
      FormInput(v-model="customerName" key-name="担当者" name="customerName" type="text" placeholder="担当者" :maxlength="100"
        :validation-rules="{ max: 100 }")
      //- receptionMemo
      FormTextarea(v-model="receptionMemo" key-name="受付メモ" name="reception-memo" placeholder="受付メモ" :maxlength="1000" :rows="7"
        :validation-rules="{ max: 1000 }" :styles="{ maxWidth: '640px', width: '640px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="onRegister" type="button") 登録
        button.btn.cancel-btn(@click="onCancel" type="button") キャンセル

  OkCancelModal(v-model="showRegisterConfirmModal" title="施設見学情報を登録しますか？" @ok="registerInspectionReservation")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoInspectionReservationList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import CustomValidate from "@/commons/utils/CustomValidate";

import InspectionReservationsService, { ApplicationType, InspectionReservationsPostModel } from "@/commons/services/InspectionReservations";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import ApprovedSwitch from "@/pc/basics/ApprovedSwitch.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

/**
 * ## 施設見学登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    ApprovedSwitch,
    FormDatetimePicker,
    FormInput,
    FormTextarea
  },
  $_veeValidate: { validator: "new" }
})
export default class InspectionReservationRegister extends Vue {
  /** 施設見学サービス */
  @Inject("InspectionReservationsService") inspectionReservationsService!: InspectionReservationsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterConfirmModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 申込み日時 */
  applicationDatetime: number | null = null;
  /** 申込み日時(日付) */
  applicationDatetimeDate: string = "";
  /** 申込み日時(開始時刻) */
  applicationDatetimeTime: string = "";
  /** 施設見学、受付状態 */
  approved: boolean = false;
  /** 見学日時 */
  datetime: number | null = null;
  /** 見学日時(日付) */
  datetimeDate: string = "";
  /** 見学日時(開始時刻) */
  datetimeTime: string = "";
  /** 氏名 */
  name: string = "";
  /** 電話番号 */
  tel: string = "";
  /** 申込者からの連絡事項 */
  applicantMessage: string = "";
  /** 担当者 */
  customerName: string = "";
  /** 施設見学、受付メモ */
  receptionMemo: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  get _requiredDatetime(): boolean {
    return this.approved;
  }

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public mounted(): void {
    this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 申込み日時はカレンダー上の初期値を作成する
   * - 見学日時はカレンダー上の初期値を作成する
   */
  public createView(): void {
    const applicationDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const applicationDatetimeTime: string = "10:00:00";
    this.applicationDatetimeDate = applicationDatetimeDate;
    this.applicationDatetimeTime = applicationDatetimeTime;

    const datetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const datetimeTime: string = "10:00:00";
    this.datetimeDate = datetimeDate;
    this.datetimeTime = datetimeTime;

    return;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   * - 受付状態が「受付済」の場合は見学日時の必須チェックを行う
   */
  public async onRegister(): Promise<void> {
    this.$validator.errors.remove("datetime");

    const validationResult: boolean = await this.$validator.validateAll();

    let datetimeValidationResult: boolean = true;
    if (this.approved == true && this.datetime == null) {
      this.$validator.errors.add({ field: "datetime", msg: "受付状態を受付済にする場合、見学日時は必須項目です" });
      datetimeValidationResult = false;
    }

    if (datetimeValidationResult && validationResult) {
      this.showValidationAlert = false;
      this.showRegisterConfirmModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 施設見学を登録する
   */
  public async registerInspectionReservation(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const requestBody: InspectionReservationsPostModel = this.createRequestBody();
    await this.inspectionReservationsService.post(userStore, requestBody);

    this.showRegisterSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 施設見学登録のリクエストボディを作成する
   */
  public createRequestBody(): InspectionReservationsPostModel {
    return {
      applicationDatetime: this.applicationDatetime as number,
      applicationType: ApplicationType.FACILITY,
      approved: this.approved,
      datetime: this.datetime,
      name: this.name,
      tel: this.tel,
      applicantMessage: this.applicantMessage ? this.applicantMessage : null,
      customerName: this.customerName ? this.customerName : null,
      receptionMemo: this.receptionMemo ? this.receptionMemo : null
    };
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.gotoInspectionReservationList();
    return;
  }

  /**
   * ### 施設見学一覧に遷移する
   */
  public gotoInspectionReservationList(): void {
    this.$router.push(PcUrl.InspectionReservationList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#inspection-reservation-register {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .approved-name {
    height: 27px;
    margin-bottom: 4px;
    font-size: 18px;
    font-weight: bold;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
